// src/admin/admin/Dashboard.jsx
import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  Typography,
  Button,
  Divider,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import {
  People,
  AccountBalanceWallet,
  SwapHoriz,
  VerifiedUser,
  MonetizationOn,
  NotificationsActive,
} from "@mui/icons-material";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  AreaChart,
  Area,
} from "recharts";
import {
  getDashboardStats,
  getTransactionTrends,
  getBalanceGrowth,
} from "../utils/api";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [balanceGrowth, setBalanceGrowth] = useState([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    getDashboardStats()
      .then((res) => setStats(res.data))
      .catch((err) => console.error("Failed to load stats:", err));

    getTransactionTrends()
      .then((res) => setTransactions(res.data))
      .catch((err) => console.error("Failed to load transactions:", err));

    getBalanceGrowth()
      .then((res) => setBalanceGrowth(res.data))
      .catch((err) => console.error("Failed to load balance growth:", err));
  }, []);

  const cardData = stats
    ? [
        {
          component: RouterLink,
          to: "/admin/users",
          title: "Total Users",
          value: stats.total_users,
          change: "+5.2%",
          icon: <People fontSize="large" />,
          color: "#00e5ff",
        },
        {
          title: "Total Balance",
          value: stats.total_balance,
          change: "+12.8%",
          icon: <AccountBalanceWallet fontSize="large" />,
          color: "#fdd835",
        },
        {
          component: RouterLink,
          to: "/admin/transactions",
          title: "Pending Transfers",
          value: stats.pending_transfers,
          change: "-3.4%",
          icon: <SwapHoriz fontSize="large" />,
          color: "#f06292",
        },
        {
          component: RouterLink,
          to: "/admin/kyc",
          title: "KYC Pending",
          value: stats.kyc_pending,
          change: "-1.1%",
          icon: <VerifiedUser fontSize="large" />,
          color: "#4caf50",
        },
        {
          component: RouterLink,
          to: "/admin/currencies",
          title: "Currencies Active",
          value: stats.currencies_active,
          change: "+2.0%",
          icon: <MonetizationOn fontSize="large" />,
          color: "#42a5f5",
        },
        {
          title: "System Alerts",
          value: stats.system_alerts,
          change: "+7.6%",
          icon: <NotificationsActive fontSize="large" />,
          color: "#ff9800",
        },
      ]
    : [];

  return (
    <Box sx={{ px: isMobile ? 1.5 : 2, py: isMobile ? 2 : 3 }}>
      {/* HEADER */}
      <Card
        sx={{
          mb: 4,
          p: isMobile ? 2 : 4,
          borderRadius: 4,
          bgcolor: theme.palette.background.paper,
          boxShadow: `0 0 25px ${
            theme.palette.mode === "dark"
              ? "rgba(0,229,255,0.2)"
              : "rgba(0,0,0,0.05)"
          }`,
          backdropFilter: "blur(12px)",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Welcome Back, Admin
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Here’s your latest system overview and financial activity.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Button variant="contained" sx={{ bgcolor: "#00e5ff", color: "#000", borderRadius: 3 }}>
            Deposit
          </Button>
          <Button variant="contained" sx={{ bgcolor: "#fdd835", color: "#000", borderRadius: 3 }}>
            Withdraw
          </Button>
          <Button variant="outlined" sx={{ borderRadius: 3 }}>
            Transfer
          </Button>
        </Box>
      </Card>

      {/* STATS CARDS */}
      <Grid container spacing={2}>
        {cardData.map((card, index) => (
          <Grid
            item
            xs={12}
            sm={6}
            lg={3}
            key={index}
            sx={{
              display: "flex", // Ensures cards stretch full width
            }}
          >
            <motion.div
              style={{ width: "100%" }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.15, duration: 0.5 }}
            >
              <Card
                component={card.component || "div"}
                to={card.to || ""}
                sx={{
                  flex: 1,
                  p: 3,
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 4,
                  bgcolor: theme.palette.background.paper,
                  boxShadow: `0 8px 25px ${card.color}44`,
                  border: `1px solid ${card.color}`,
                  transition: "0.3s",
                  "&:hover": { transform: "translateY(-8px) scale(1.03)" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: "50%",
                      bgcolor: `${card.color}22`,
                      color: card.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {card.icon}
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary }}>
                      {card.title}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: "bold", color: card.color }}>
                      <CountUp end={card.value || 0} duration={2} separator="," />
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Chip
                    label={card.change}
                    size="small"
                    sx={{
                      bgcolor: card.change.includes("-")
                        ? theme.palette.error.light
                        : theme.palette.success.light,
                      color: card.change.includes("-")
                        ? theme.palette.error.main
                        : theme.palette.success.main,
                    }}
                  />
                  <Button size="small" variant="outlined" sx={{ borderColor: card.color, color: card.color }}>
                    View Details
                  </Button>
                </Box>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* CHARTS */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} lg={6}>
          <Card sx={{ p: 3, borderRadius: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Transaction Trends
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={transactions}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="transactions" stroke="#00e5ff" strokeWidth={3} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card sx={{ p: 3, borderRadius: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Balance Growth
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={balanceGrowth}>
                <defs>
                  <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#fdd835" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#fdd835" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="balance"
                  stroke="#fdd835"
                  strokeWidth={3}
                  fill="url(#balanceGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}